import { useEffect, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Library } from "lucide-react";
import { api } from "@/lib/api";
import { resolveMediaUrl, uploadMedia } from "@/lib/media";

/**
 * ImagePicker — drag & drop upload + media gallery picker.
 * value: the stored URL (relative /api/media/... or absolute)
 * onChange(url): called with new URL string ("" to clear).
 */
export default function ImagePicker({ value, onChange, label = "Imagen" }) {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showLibrary, setShowLibrary] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError(""); setBusy(true);
    try {
      const res = await uploadMedia(file);
      onChange(res.url);
    } catch (e) {
      setError(e.message || "Error al subir");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div data-testid="image-picker">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs uppercase tracking-widest font-bold">{label}</label>
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowLibrary(true)} className="text-xs text-primary font-semibold uppercase tracking-widest inline-flex items-center gap-1 hover:underline">
            <Library className="w-3 h-3" /> Galería
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} className="text-xs text-destructive font-semibold uppercase tracking-widest inline-flex items-center gap-1 hover:underline">
              <X className="w-3 h-3" /> Quitar
            </button>
          )}
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-sm cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary"} ${value ? "p-2" : "p-6"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {value ? (
          <div className="flex items-center gap-3">
            <img src={resolveMediaUrl(value)} alt="" className="w-20 h-20 object-cover rounded-sm border border-border" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground truncate">{value}</p>
              <p className="text-[10px] uppercase tracking-widest text-primary mt-1">Click para reemplazar</p>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted-foreground">
            {busy ? (
              <p className="text-sm">Subiendo...</p>
            ) : (
              <>
                <Upload className="w-6 h-6 mx-auto mb-2" />
                <p className="text-sm font-semibold">Arrastrá una imagen o click para subir</p>
                <p className="text-xs mt-1">PNG, JPG, WEBP · máx 5MB</p>
              </>
            )}
          </div>
        )}
      </div>

      <div className="mt-2">
        <input
          type="text"
          placeholder="...o pegá una URL externa"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-9 px-3 bg-background border border-border rounded-sm text-xs focus:border-primary outline-none"
        />
      </div>

      {error && <p className="text-xs text-destructive mt-1">{error}</p>}

      {showLibrary && <MediaLibrary onPick={(url) => { onChange(url); setShowLibrary(false); }} onClose={() => setShowLibrary(false)} />}
    </div>
  );
}

function MediaLibrary({ onPick, onClose }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/media").then(setItems).catch(() => setItems([])); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-background border border-border rounded-sm w-full max-w-5xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-primary" />
            <h3 className="font-display font-bold text-lg">Galería de medios</h3>
            <span className="text-xs text-muted-foreground">({items.length})</span>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-auto p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {items.map((it) => (
            <button key={it.id} onClick={() => onPick(it.url)} className="group border border-border rounded-sm overflow-hidden hover:border-primary transition-colors">
              <div className="aspect-square bg-secondary">
                <img src={resolveMediaUrl(it.url)} alt={it.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-2 text-xs truncate text-left">{it.name}</div>
            </button>
          ))}
          {items.length === 0 && <p className="col-span-full text-center text-muted-foreground py-12 text-sm">Aún no hay imágenes. Subí una desde el campo de arriba o desde Admin → Medios.</p>}
        </div>
      </div>
    </div>
  );
}
